import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ExternalLink,
  Loader2,
  MoreVertical,
  Share2,
  Star,
  Trash2,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConfirm } from "@/components/shared/ConfirmDialog";
import { useToast } from "@/components/shared/Toast";
import { useSharedGroupChatSessions } from "@/context/SharedGroupChatSessionsContext";
import { groupChatService } from "@/lib/groupChatService";
import type { GroupChatSession } from "@/types";

interface GroupChatHistorySidebarProps {
  currentSessionId?: string;
  refreshKey?: number;
  onSessionDeleted?: (uuid: string) => void;
}

export function GroupChatHistorySidebar({
  currentSessionId,
  refreshKey,
  onSessionDeleted,
}: Readonly<GroupChatHistorySidebarProps>) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { sharedSessions, isLoading: isLoadingShared } = useSharedGroupChatSessions();
  const [activeTab, setActiveTab] = useState<"mine" | "shared">("mine");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sessions, setSessions] = useState<GroupChatSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, [refreshKey]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => setMenuOpenId(null);
    if (menuOpenId) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [menuOpenId]);

  const loadSessions = async () => {
    try {
      setIsLoading(true);
      const response = await groupChatService.listSessions();
      setSessions(response.data);
    } catch (err) {
      console.error("Failed to load sessions:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionClick = (sessionUuid: string) => {
    navigate(`/group-chat/${sessionUuid}`);
  };

  const handleToggleStar = async (e: React.MouseEvent, session: GroupChatSession) => {
    e.stopPropagation();
    setMenuOpenId(null);
    try {
      await groupChatService.updateSession(session.uuid, {
        isStarred: !session.isStarred,
      });
      setSessions((prev) => prev.map((s) => (s.uuid === session.uuid ? { ...s, isStarred: !s.isStarred } : s)));
      showToast(session.isStarred ? "Discussion unstarred" : "Discussion starred", "success");
    } catch (err) {
      console.error("Failed to toggle star:", err);
      showToast("Failed to update discussion", "error");
    }
  };

  const handleDelete = async (e: React.MouseEvent, session: GroupChatSession) => {
    e.stopPropagation();
    setMenuOpenId(null);

    const confirmed = await confirm({
      title: "Delete Discussion",
      message: `Are you sure you want to delete "${session.title || "this discussion"}"? This action cannot be undone.`,
      confirmText: "Delete",
      variant: "danger",
    });

    if (!confirmed) return;

    try {
      await groupChatService.deleteSession(session.uuid);
      setSessions((prev) => prev.filter((s) => s.uuid !== session.uuid));
      showToast("Discussion deleted", "success");
      onSessionDeleted?.(session.uuid);
    } catch (err) {
      console.error("Failed to delete session:", err);
      showToast("Failed to delete discussion", "error");
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  if (isCollapsed) {
    return (
      <div className="h-full w-12 bg-surface-primary border-l border-border-primary flex flex-col items-center py-4">
        <button
          onClick={() => setIsCollapsed(false)}
          className="p-2 bg-surface-secondary hover:bg-interactive-hover border border-border-secondary rounded-lg transition text-content-tertiary hover:text-content-primary"
          title="Expand history"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Clock className="w-5 h-5 text-content-quaternary rotate-90" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-80 bg-surface-primary border-l border-border-primary flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border-primary flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-content-tertiary" />
          <h2 className="text-content-primary font-semibold">Discussions</h2>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1.5 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
          title="Collapse history"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border-primary">
        <button
          onClick={() => setActiveTab("mine")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition ${
            activeTab === "mine"
              ? "text-brand-primary border-b-2 border-brand-primary"
              : "text-content-tertiary hover:text-content-primary"
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          Recent
        </button>
        <button
          onClick={() => setActiveTab("shared")}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-medium transition ${
            activeTab === "shared"
              ? "text-brand-primary border-b-2 border-brand-primary"
              : "text-content-tertiary hover:text-content-primary"
          }`}
        >
          <Share2 className="w-3.5 h-3.5" />
          Shared with me
          {sharedSessions.length > 0 && (
            <span className="ml-1 px-1.5 py-0.5 bg-brand-primary-subtle text-brand-primary rounded-full text-xs">
              {sharedSessions.length}
            </span>
          )}
        </button>
      </div>

      {/* Sessions List */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === "mine"
          ? (() => {
              if (isLoading) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                  </div>
                );
              }
              if (sessions.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <Users className="w-12 h-12 text-surface-tertiary mb-3" />
                    <p className="text-content-tertiary text-sm">No discussions yet</p>
                    <p className="text-content-quaternary text-xs mt-1">Start a group chat to see it here</p>
                  </div>
                );
              }
              return (
                <div className="p-2">
                  {sessions.map((session) => {
                    const isActive = session.uuid === currentSessionId;
                    const isMenuOpen = menuOpenId === session.uuid;
                    return (
                      <div key={session.uuid} className="relative mb-1">
                        <button
                          type="button"
                          onClick={() => handleSessionClick(session.uuid)}
                          className={`w-full text-left p-3 rounded-lg transition group cursor-pointer ${
                            isActive
                              ? "bg-brand-primary-subtle border border-brand-primary/30"
                              : "hover:bg-interactive-hover border border-transparent"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3
                              className={`text-sm font-medium line-clamp-2 flex-1 ${
                                isActive ? "text-brand-primary" : "text-content-primary"
                              }`}
                            >
                              {session.title || "New Discussion"}
                            </h3>
                            <div className="flex items-center gap-1 shrink-0">
                              {session.isStarred && <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpenId(isMenuOpen ? null : session.uuid);
                                }}
                                className="p-1 rounded-sm hover:bg-interactive-active text-content-quaternary hover:text-content-primary opacity-0 group-hover:opacity-100 transition-opacity"
                                title="More options"
                              >
                                <MoreVertical className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs text-content-quaternary">
                              <Users className="w-3.5 h-3.5" />
                              <span>{session.messageCount || 0}</span>
                              {session.isActive && (
                                <span className="ml-1 px-1.5 py-0.5 bg-green-500/20 text-green-400 rounded-sm text-xs">
                                  Active
                                </span>
                              )}
                            </div>
                            <span className="text-xs text-content-quaternary">
                              {formatDate(session.updatedAt || session.createdAt)}
                            </span>
                          </div>
                        </button>
                        {isMenuOpen && (
                          <div className="absolute right-2 top-10 z-50 bg-surface-secondary border border-border-secondary rounded-lg shadow-xl py-1 min-w-[160px]">
                            <button
                              onClick={(e) => handleToggleStar(e, session)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:bg-interactive-active hover:text-content-primary transition"
                            >
                              <Star
                                className={`w-4 h-4 ${
                                  session.isStarred ? "text-yellow-500 fill-yellow-500" : "text-content-tertiary"
                                }`}
                              />
                              {session.isStarred ? "Unstar" : "Star"}
                            </button>
                            <button
                              onClick={(e) => handleDelete(e, session)}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-interactive-active hover:text-red-300 transition"
                            >
                              <Trash2 className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()
          : /* Shared with me tab */
            (() => {
              if (isLoadingShared) {
                return (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
                  </div>
                );
              }
              if (sharedSessions.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <Share2 className="w-12 h-12 text-surface-tertiary mb-3" />
                    <p className="text-content-tertiary text-sm">No shared discussions</p>
                    <p className="text-content-quaternary text-xs mt-1">Discussions shared with you will appear here</p>
                  </div>
                );
              }
              return (
                <div className="p-2">
                  {sharedSessions.map((session) => {
                    const isActive = session.uuid === currentSessionId;
                    return (
                      <div key={session.uuid} className="mb-1">
                        <button
                          type="button"
                          onClick={() => navigate(`/group-chat/${session.uuid}`)}
                          className={`w-full text-left p-3 rounded-lg transition cursor-pointer ${
                            isActive
                              ? "bg-brand-primary-subtle border border-brand-primary/30"
                              : "hover:bg-interactive-hover border border-transparent"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h3
                              className={`text-sm font-medium line-clamp-2 flex-1 ${
                                isActive ? "text-brand-primary" : "text-content-primary"
                              }`}
                            >
                              {session.title || "Untitled Discussion"}
                            </h3>
                          </div>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs text-content-quaternary">
                              <Users className="w-3.5 h-3.5" />
                              <span>{session.messageCount || 0}</span>
                            </div>
                            <span className="text-xs text-content-quaternary">
                              {formatDate(session.updatedAt || session.createdAt)}
                            </span>
                          </div>
                          <div className="mt-1.5 text-xs text-content-quaternary">
                            Shared by {session.sharing.sharedBy.name}
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
      </div>

      {/* Footer — pb-24 ensures clearance above the fixed Feedback button */}
      <div className="p-3 pb-24 border-t border-border-primary">
        <button
          onClick={() => navigate("/group-chat")}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-surface-secondary hover:bg-interactive-active text-content-primary rounded-lg transition text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          View All Discussions
        </button>
      </div>
    </div>
  );
}
